import { useCallback, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import { SHEET_SLOP_PX, sheetOffset, shouldDismissSheet } from './sheetDismiss';

type SheetDismissOptions = {
  /** Сама шторка — её и двигаем за пальцем. */
  ref: RefObject<HTMLElement | null>;
  /** На десктопе это окно по центру, тянуть его вниз нечего. */
  enabled: boolean;
  /** Отпустили достаточно далеко или резко — закрываем. */
  onDismiss: () => void;
};

/** Возврат на место, когда жеста не хватило: короткая дуга, без пружины. */
const SNAP_BACK = 'transform 200ms cubic-bezier(.32,.72,0,1)';

/**
 * Утянуть шторку вниз, чтобы закрыть.
 *
 * Полоска-ручка у шторки была с самого начала, а жеста за ней не стояло: она обещала движение,
 * которого не существовало ни в приложении, ни в мобильном вебе. Здесь именно оно.
 *
 * Три вещи, без которых жест мёртв на телефоне, и все три — уроки окна разговора
 * (`features/media-window/useCornerDrag`), где та же механика уже прошла через устройства:
 *
 * 1. `touch-action: none` на области захвата. Иначе браузер забирает вертикальное движение себе
 *    под прокрутку и до обработчика оно не доходит вовсе.
 * 2. `overscroll-behavior: contain` там же. Иначе на iOS тянется страница целиком — резинкой, —
 *    а шторка стоит на месте.
 * 3. Захват вешается на ручку и шапку, но **не** на тело шторки: в теле живёт прокрутка длинных
 *    форм, и жест отнял бы её.
 *
 * `preventDefault` здесь нет намеренно, хотя в окне разговора он есть. Там за ручку отвечает
 * отдельная полоска без кнопок, здесь же ручкой работает и шапка, а в шапке стоит крестик:
 * `preventDefault` на pointerdown отнял бы у него нажатие. Поэтому от случайного смещения
 * защищает порог, а не запрет действия по умолчанию.
 */
export const useSheetDismiss = ({ ref, enabled, onDismiss }: SheetDismissOptions) =>
  useCallback(
    (event: ReactPointerEvent) => {
      const el = ref.current;
      if (!enabled || !el || !event.isPrimary) {
        return;
      }

      const startY = event.clientY;
      const startedAt = Date.now();
      // Высота снимается один раз на старте: пока палец ведёт, шторка не меняет размер, а вот
      // чтение геометрии на каждом кадре заставляло бы браузер пересчитывать раскладку.
      const height = el.getBoundingClientRect().height;
      let moved = 0;

      const onMove = (move: PointerEvent) => {
        const dy = move.clientY - startY;
        moved = Math.abs(dy);
        if (moved < SHEET_SLOP_PX) {
          return;
        }
        el.style.transition = 'none';
        el.style.transform = `translateY(${sheetOffset(dy)}px)`;
      };

      const onUp = (up: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        const dy = up.clientY - startY;

        if (!shouldDismissSheet({ dy, elapsedMs: Date.now() - startedAt, height })) {
          // Движения не хватило — возвращаем на место дугой.
          el.style.transition = SNAP_BACK;
          el.style.transform = '';
          return;
        }

        /**
         * Закрываем — и свой сдвиг НЕ снимаем.
         *
         * Анимация выхода у Chakra идёт по свойству `translate`, а мы двигаем `transform`: это
         * разные свойства, и браузер их складывает, а не выбирает одно. Поэтому шторка просто
         * продолжает путь оттуда, где её отпустил палец.
         *
         * Снятый сдвиг давал разрыв: шторка на кадр возвращалась в полную высоту — успевала
         * мигнуть целиком — и лишь потом уезжала анимацией. На iOS этого не видно из-за иного
         * порядка кадров, на Android видно отчётливо.
         */
        onDismiss();

        // Единственный случай, когда сдвиг придётся снять самим: вызывающий отказался закрываться
        // (спросил про несохранённое). Тогда шторка остаётся открытой и не должна остаться
        // сдвинутой. Проверяем на следующем кадре — к нему состояние уже проставлено.
        requestAnimationFrame(() => {
          if (el.isConnected && el.dataset.state !== 'closed') {
            el.style.transition = SNAP_BACK;
            el.style.transform = '';
          }
        });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [ref, enabled, onDismiss]
  );
